import ConditionTableCell from '@src/app-components/table/table-cell-components/fish/ConditionTableCell';
import CountTableCell from '@src/app-components/table/table-cell-components/fish/CountTableCell';
import FinCurlTableCell from '@src/app-components/table/table-cell-components/fish/FinCurlTableCell';
import FishLinkTableCell from '@src/app-components/table/table-cell-components/fish/FishLinkTableCell';
import FloyTagTableCell from '@src/app-components/table/table-cell-components/fish/floy-tag/FloyTagTableCell';
import FloyTagMrTableCell from '@src/app-components/table/table-cell-components/fish/floy-tag/FloyTagTableCell.mr';
import FloyTagPrefixTableCell from '@src/app-components/table/table-cell-components/fish/floy-tag/FloyTagTableCell.prefix';
import GeneticVialNumTableCell from '@src/app-components/table/table-cell-components/fish/GeneticVialNumTableCell';
import LengthTableCell from '@src/app-components/table/table-cell-components/fish/LengthTableCell';
import PanelHookTableCell from '@src/app-components/table/table-cell-components/fish/PanelHookTableCell';
import TagnumberTableCell from '@src/app-components/table/table-cell-components/fish/TagnumberTableCell';
import WeightTableCell from '@src/app-components/table/table-cell-components/fish/WeightTableCell';
import { TableCell } from '@src/app-components/table/table-cell-components/TableCell';
import { CreateComboboxOptions, createDropdownOptions } from '@src/app-pages/data-entry/dataEntryHelper';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@trussworks/react-uswds';
import { mdiDeleteOutline } from '@mdi/js';
import Icon from '@src/app-components/icon/icon';

const getRowIndex = (val) => val?.split('-')?.at('-1');

export const getFishColumns = ({
  gear,
  speciesOptions,
  lengthTypes,
  floyTagPrefixes,
  markRecaptureOptions,
  yesNoOptions,
  fishStructures,
  panelHookRequired,
  canDeleteRow,
  onRemoveRow,
}) => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor('fid', {
      header: 'Fish ID',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 90,
    }),
    columnHelper.accessor(
      (row) => ({
        fieldId: row?.fFid ?? '',
        localDisplayId: row?._status !== 'synced' ? (row?.localDisplayId ?? '') : '',
      }),
      {
        id: 'fFid',
        header: 'Field ID',
        sortingFn: (rowA, rowB) => {
          const firstFieldId = String(
            rowA.original?.fFid ?? rowA.original?.f_fid ?? rowA.original?.localDisplayId ?? ''
          ).trim();
          const secondFieldId = String(
            rowB.original?.fFid ?? rowB.original?.f_fid ?? rowB.original?.localDisplayId ?? ''
          ).trim();

          if (!firstFieldId) return secondFieldId ? 1 : 0;
          if (!secondFieldId) return -1;
          return firstFieldId.localeCompare(secondFieldId, undefined, { numeric: true, sensitivity: 'base' });
        },
        cell: ({ cell }) => {
          const value = cell.getValue();

          if (value?.fieldId) {
            return <span>{getRowIndex(value.fieldId)}</span>;
          }

          return <span>{value?.localDisplayId ?? ''}</span>;
        },
        size: 60,
      }
    ),
    columnHelper.accessor('supplementalData', {
      header: 'Supple- mental',
      cell: FishLinkTableCell,
      size: 10,
      enableSorting: false,
      meta: {
        centerText: true,
        optional: true, // These values are set to prevent error styling from rendering when inappropriate.
      },
    }),
    columnHelper.accessor('panelHook', {
      header: 'Panel/ Hook',
      cell: PanelHookTableCell,
      size: 60,
      meta: {
        required: panelHookRequired,
        gear: gear,
      },
    }),
    columnHelper.accessor('species', {
      header: 'Species',
      cell: TableCell,
      meta: {
        type: 'combobox',
        required: true,
        options: (row) => CreateComboboxOptions(speciesOptions(row)),
        width: '13rem',
      },
    }),
    columnHelper.accessor('length', {
      header: 'Length (mm)',
      cell: LengthTableCell,
      size: 65,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('lengthType', {
      header: 'Length Type',
      cell: TableCell,
      meta: {
        type: 'select',
        options: createDropdownOptions(lengthTypes),
      },
    }),
    columnHelper.accessor('weight', {
      header: 'Weight (grams)',
      cell: WeightTableCell,
      size: 65,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('countF', {
      header: 'Count',
      cell: CountTableCell,
      size: 60,
      minSize: 60,
      maxSize: 80,
    }),
    columnHelper.accessor('ftPrefix', {
      header: 'Floy Tag Prefix',
      cell: FloyTagPrefixTableCell,
      size: 140,
      minSize: 140,
      maxSize: 150,
      meta: {
        options: createDropdownOptions(floyTagPrefixes),
      },
    }),
    columnHelper.accessor('floyTag', {
      header: 'Floy Tag',
      cell: FloyTagTableCell,
      size: 85,
    }),
    columnHelper.accessor('mR', {
      header: 'Floy Tag M/R',
      cell: FloyTagMrTableCell,
      size: 150,
      minSize: 150,
      maxSize: 180,
      meta: {
        options: createDropdownOptions(markRecaptureOptions),
      },
    }),
    columnHelper.accessor('geneticsVialNumber', {
      header: 'Genetics Vial #',
      cell: GeneticVialNumTableCell,
      size: 170,
    }),
    columnHelper.accessor('condition', {
      header: 'Condition',
      cell: ConditionTableCell,
      size: 100,
    }),
    columnHelper.accessor('tagnumber', {
      header: 'Tag Number',
      cell: TagnumberTableCell,
      size: 125,
    }),
    columnHelper.accessor('finCurl', {
      header: 'Fin Curl',
      cell: FinCurlTableCell,
      size: 150,
      meta: {
        type: 'select',
        options: yesNoOptions,
      },
    }),
    columnHelper.accessor('otolith', {
      header: 'Otolith',
      cell: TableCell,
      size: 200,
      meta: {
        type: 'select',
        options: createDropdownOptions(fishStructures),
      },
    }),
    // NOTE: Not in requirements, but display historic data
    columnHelper.accessor('raySpine', {
      header: 'Ray Spine',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 200,
    }),
    columnHelper.accessor('KN', {
      header: 'KN',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 200,
    }),
    columnHelper.accessor('RSD', {
      header: 'RSD',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 200,
    }),
    columnHelper.accessor('editInitials', {
      header: 'Edit Initials',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 200,
    }),
    columnHelper.accessor('uploadedBy', {
      header: 'Uploaded By',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 200,
    }),
    columnHelper.display({
      header: 'Delete',
      cell: ({ row }) =>
        canDeleteRow(row.original) ? (
          <Button
            aria-label='Delete unsaved fish row'
            className='secondary-btn'
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this fish row?')) {
                onRemoveRow(row.index);
              }
            }}
            size='small'
            title='Delete unsaved fish row'
            type='button'
          >
            <Icon focusable={false} path={mdiDeleteOutline} size='16px' />
          </Button>
        ) : null,
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
    }),
  ];
};
